import {
  AssetOhouseHorizontalSvg,
  AssetOhouseHorizontalWhiteSvg,
} from '@bucketplace/assets';
import { bucketLog } from '@bucketplace/bucket-log';
import { bucketClickLog } from '@bucketplace/common';
import {
  BREAK_POINT,
  Flex,
  FONT_STYLE,
  MEDIA_QUERY,
  ZINDEX,
} from '@bucketplace/design-system';
import { IconPoint3ConnectedSplitPath, IconX } from '@bucketplace/icons';
import { sendDeepLinkAction } from '@bucketplace/owap-sdk';
import { getVar, ThemeProvider, useTheme } from '@bucketplace/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Slide, ToastContainer } from 'react-toastify';
import { getShortLink } from '@/api/share-link';
import { HOST, OHS_HOST } from '@/common';
import { useOwapInfo } from '@/hooks';
import { useCopyClipboard } from '@/hooks/useCopyClipboard';
import { extractDomain, getAppOsInfo } from '@/utils';
import { getDeepLink } from '@/utils/getDeepLink';
import { REBRANDING_THEME } from './style';

export function RebrandPageLayout({
  children,
}: PropsWithChildren): ReactElement {
  const [footerHeight, setFooterHeight] = useState<number>(0);
  const footerRef = useRef<HTMLDivElement>(null);
  const isAndroid = getAppOsInfo()?.osType === 'Android';
  const { isOwap } = useOwapInfo();

  useLayoutEffect(() => {
    if (!footerRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        setFooterHeight(height);
      }
    });

    resizeObserver.observe(footerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [footerRef]);

  return (
    <ThemeProvider tokens={REBRANDING_THEME}>
      <ContainerColumn>
        {!isOwap && <SimpleBrandHeader />}
        <ContainerRow footerHeight={footerHeight}>
          <ContentsContainer>
            <PageWrap>{children}</PageWrap>
          </ContentsContainer>
        </ContainerRow>
        <ToastContainer
          position="bottom-center"
          hideProgressBar
          autoClose={2000}
          transition={Slide}
          style={{
            marginBottom: isAndroid ? '42px' : '0px',
            zIndex: 10001,
          }}
        />
      </ContainerColumn>
    </ThemeProvider>
  );
}

export function SimpleBrandHeader(): ReactElement {
  const { asPath } = useRouter();
  const { cssVar } = useTheme<typeof REBRANDING_THEME>();
  const { isOwap } = useOwapInfo();
  const [pathname] = asPath.split('?');

  const { onCopy } = useCopyClipboard();

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleClickShare = async (): Promise<void> => {
    bucketClickLog({
      type: 'button',
      id: 'SHARE',
      section_id: 'TOP',
    });

    const domain = extractDomain(HOST);
    // NOTE: owap webview type이 page로 하드코딩 되어 있어서 별도의 유틸함수 생성
    const deepLinkUrl = getDeepLink({
      host: domain,
      pathname,
      isOwap: true,
      owapWebviewType: 'fullpage',
    });

    onCopy(async () => {
      try {
        return await getShortLink(deepLinkUrl);
      } catch {
        return deepLinkUrl;
      }
    });
  };

  if (isOwap) {
    return (
      <Flex
        position="fixed"
        direction="row"
        h="57px"
        left={0}
        right={0}
        p="16px 20px"
        justify="space-between"
        zIndex={ZINDEX.appBar}
        css={{
          backdropFilter: 'blur(35px)',
        }}
      >
        <HeaderIcon />
        <Flex direction="row" gap={16}>
          <IconButton onClick={handleClickShare}>
            <IconPoint3ConnectedSplitPath
              weight="regular"
              color={getVar(cssVar('share-btn-foreground'))}
              size={24}
            />
          </IconButton>
          <IconButton
            onClick={() => {
              sendDeepLinkAction('close', {});
            }}
          >
            <IconX
              weight="regular"
              color={getVar(cssVar('share-btn-foreground'))}
              size={24}
            />
          </IconButton>
        </Flex>
      </Flex>
    );
  }

  return (
    <Header>
      <Flex
        direction="row"
        w="100%"
        h="100%"
        maw={BREAK_POINT.laptop}
        justify="space-between"
        p={{
          mobile: '16px 20px',
          tablet: '16px 20px',
          laptop: '10px 20px',
          desktop: '10px 0',
        }}
      >
        <HeaderIcon />
        <Button
          type="button"
          variant={{
            pc: getVar(cssVar('share-btn-foreground-inverse')),
            mobile: getVar(cssVar('share-btn-foreground')),
          }}
          bgc={{
            pc: getVar(cssVar('share-btn-background')),
            mobile: 'transparent',
          }}
          opacity={isClient ? 1 : 0}
          onClick={handleClickShare}
        >
          공유하기
        </Button>
      </Flex>
    </Header>
  );
}

function HeaderIcon(): ReactElement {
  const router = useRouter();
  const theme = useTheme<typeof REBRANDING_THEME>();
  const OhouseHorizontalIcon =
    theme.appearance === 'light'
      ? AssetOhouseHorizontalSvg
      : AssetOhouseHorizontalWhiteSvg;
  const { isOwap } = useOwapInfo();

  const handleClick = (): void => {
    bucketLog({
      category: 'click',
      type: 'button',
      section_id: 'TOP',
      id: 'OHOUSE_LOGO',
    });

    if (isOwap) {
      sendDeepLinkAction('closeMove', {
        url: OHS_HOST,
        isNative: true,
      });
      return;
    }

    router.push(OHS_HOST);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{ display: 'flex', alignItems: 'center', height: '100%' }}
    >
      <OhouseHorizontalIcon
        aspect="height"
        height="100%"
        css={{
          maxHeight: '30px',
          minHeight: '25px',
        }}
      />
    </button>
  );
}

/** 웹뷰 아이콘 버튼 */
function IconButton({
  onClick,
  children,
}: PropsWithChildren<{ onClick: () => void }>): ReactElement {
  return (
    <button type="button" onClick={onClick}>
      {children}
    </button>
  );
}

const ContainerColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;

const ContainerRow = styled.div<{ footerHeight: number }>`
  display: flex;
  width: 100%;
  overflow: hidden;
`;

const ContentsContainer = styled.div`
  width: 100%;
  @media screen and (min-width: 768px) {
    margin: 0 auto;
    box-sizing: border-box;
    flex: 1 0 auto;
  }
`;

const PageWrap = styled.div`
  width: 100%;
`;

const Header = styled.header`
  display: flex;
  position: fixed;
  width: 100%;
  height: 57px;
  align-items: center;
  justify-content: center;
  z-index: ${ZINDEX.banner};
  backdrop-filter: blur(30px);

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    height: 54px;
  `,
  )}
`;

const Button = styled.button<{
  variant: { pc: string; mobile: string };
  bgc: { pc: string; mobile: string };
  opacity: number;
}>`
  height: 24px;
  ${FONT_STYLE.body16L24}
  font-weight: 600;
  color: ${({ variant }) => variant.mobile};
  opacity: ${({ opacity }) => opacity};

  transition: color .3s ease,background-color .3s ease;

  ${({ variant, bgc }) =>
    MEDIA_QUERY.min(
      'laptop',
      css`
    background-color: ${bgc.pc};
    padding: 8px 12px;
    border-radius: 8px;
    height: 36px;

    ${FONT_STYLE.body14L20}
    font-weight: 700;
    color: ${variant.pc};
  `,
    )}
`;
